// src/components/Enrollments/Enrollments.jsx
// ============================================================================
// BLOCK ENR.PAGE.0 — Enrollments feature page
// ----------------------------------------------------------------------------
// PURPOSE
//  - React replacement for the legacy Enrollments ASPX page.
//  - Modeled after OutstandingOrders.jsx, but tailored to the legacy report.
// ============================================================================

import React from 'react';

import Box from '@mui/material/Box';
import TablePagination from '@mui/material/TablePagination';

import Alert from '../common/Alert.jsx';
import PageLoading from '../common/PageLoading.jsx';
import EnrollmentsHeader from './components/EnrollmentsHeader.jsx';
import EnrollmentsTable from './components/EnrollmentsTable.jsx';
import { ROWS_PER_PAGE_OPTIONS } from './constants/enrollmentsUi.js';
import useEnrollmentsController from './hooks/useEnrollmentsController.js';

function Enrollments() {
  const {
    customerSearch,
    errorMessage,
    handleChangePage,
    handleChangeRowsPerPage,
    handleCustomerSearchChange,
    handleExport,
    handleRepChange,
    handleRepSearchChange,
    handleProductTypeSearchChange,
    handleYearChange,
    loading,
    page,
    productTypeSearch,
    repSearch,
    rows,
    rowsPerPage,
    selectedRep,
    selectedYear,
    setErrorMessage,
    totalItems,
    totals,
  } = useEnrollmentsController();

  return (
    <Box sx={{ mx: '0.25rem', mb: '0.25rem', mt: '1rem', width: '100%' }}>
      {errorMessage && (
        <Alert
          data-cy="enrollments-error"
          message={errorMessage}
          setMessage={setErrorMessage}
          level="error"
        />
      )}

      <EnrollmentsHeader
        customerSearch={customerSearch}
        loading={loading}
        onCustomerSearchChange={handleCustomerSearchChange}
        onExport={handleExport}
        onRepChange={handleRepChange}
        onRepSearchChange={handleRepSearchChange}
        onProductTypeSearchChange={handleProductTypeSearchChange}
        onYearChange={handleYearChange}
        productTypeSearch={productTypeSearch}
        repSearch={repSearch}
        selectedRep={selectedRep}
        selectedYear={selectedYear}
      />

      {selectedRep && !loading && rows.length > 0 && (
        <TablePagination
          data-cy="enrollments-top-pagination"
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          component="div"
          count={totalItems}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          showFirstButton
          showLastButton
          sx={{ mb: 1 }}
        />
      )}

      {!selectedRep && !loading && (
        <Box sx={{ mt: 4 }}>
          <Alert
            data-cy="enrollments-select-rep-message"
            message="Please select a rep above."
            setMessage={() => {}}
            level="info"
          />
        </Box>
      )}

      {loading ? (
        <Box sx={{ mt: 4, mb: 4 }}>
          <PageLoading />
        </Box>
      ) : (
        selectedRep && <EnrollmentsTable rows={rows} totals={totals} />
      )}

      {selectedRep && !loading && rows.length > 0 && (
        <TablePagination
          data-cy="enrollments-bottom-pagination"
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          component="div"
          count={totalItems}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          showFirstButton
          showLastButton
        />
      )}
    </Box>
  );
}

export default React.memo(Enrollments);
